// Libs
import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';

// Components
import CountButton from '../../Button/CountButton';
import Button from '../../Button';

// Hooks
import useKitsProducts from '../../../pages/Kits/hooks/useKitsProducts';

// Utils
import isDigitalAsset from '../../../utils/isDigitalAsset';

// Styles
import { useStyles } from './KitsProductQtyButtonsStyles';

const KitsProductQtyButtons = ({ nums, ...props }) => {
  const { product, detailsMode, isMobileMode, listView, className } = props;
  const [tempCount, setTempCount] = useState(0);
  const { selectingKitProducts } = useSelector((state) => state.kits);
  const {
    currentKit,
    updateKitProductQty,
    addNewKitProduct,
    getProductQty,
    removeOneProductFromKit,
    setCountForProductInKit,
  } = useKitsProducts();

  const { classes, cx } = useStyles({ listView, detailsMode });

  const qty = getProductQty(product?.product_id);
  const isProductInKit = !!qty;
  const count = nums ? nums : qty;
  const isDigitalAssetProduct = isDigitalAsset(product);

  const isAddToKitButtonDisabled = useMemo(() => {
    if (isDigitalAssetProduct) {
      return isProductInKit;
    }

    return isProductInKit || (!isMobileMode && tempCount === 0);
  }, [isMobileMode, isProductInKit, tempCount, isDigitalAssetProduct]);

  const addOneProductToKit = () => {
    updateKitProductQty(currentKit.id, product.product_id, count + 1);
  };

  const removeOneProduct = () => {
    removeOneProductFromKit(count, product.product_id, detailsMode);
  };

  const setCount = (product, count) => {
    setCountForProductInKit(Math.round(count), product.product_id, detailsMode);
  };

  const kitSelectingAddOne = () => {
    if (!count) {
      return () => setTempCount(tempCount + 1);
    }

    return () => addNewKitProduct(product.product_id, count + 1);
  };

  const kitSelectingRemoveOne = () => {
    if (!count) {
      return () => setTempCount(tempCount - 1);
    }

    return () => addNewKitProduct(product.product_id, count - 1);
  };

  const kitSelectingSetCount = () => {
    if (!count) {
      return (product, value) => setTempCount(Math.round(value));
    }

    return (product, value) =>
      addNewKitProduct(product.product_id, Math.round(value));
  };

  return selectingKitProducts ? (
    // buttons in material product card in "modal" while adding products in kit
    <div className={cx(classes.container, className)}>
      {(detailsMode || (!detailsMode && !isMobileMode)) && (
        <CountButton
          product={product}
          count={count || tempCount}
          onAdd={kitSelectingAddOne()}
          onRemove={kitSelectingRemoveOne()}
          onSetCount={kitSelectingSetCount()}
          limitValue={false}
          className={classes.countButton}
        />
      )}
      <Button
        id="add-to-kits"
        onClick={() => {
          let quantity;
          if (isDigitalAssetProduct || (isMobileMode && !detailsMode)) {
            quantity = 1;
          } else if (count) {
            quantity = count;
          } else {
            quantity = tempCount;
          }

          addNewKitProduct(product.product_id, quantity);
        }}
        disabled={isAddToKitButtonDisabled}
        className={classes.addToKitButton}>
        {isProductInKit ? 'In Kit' : 'Add To Kit'}
      </Button>
    </div>
  ) : (
    // buttons in kit product card, no labels and no button
    <CountButton
      product={product}
      count={count}
      limitValue={false} // user can enter any value
      onAdd={addOneProductToKit}
      onRemove={removeOneProduct}
      onSetCount={setCount}
      className={classes.countButton}
    />
  );
};

export default KitsProductQtyButtons;
